import { useCallback, useEffect, useRef, useState } from "react";
import { useDeferredValue } from "react";
import { useSelector } from "react-redux";
import {
  getSearchIndex,
  clearSort,
  setSearchPage,
  setSearchQuery,
  setSearchSelectedId,
  getSearchState,
} from "src/features/projectGroups/state/projectGroupsSlice";
import { useAppDispatch } from "src/common/state/hooks";
import { fetchProjectGroupsWithProjects } from "src/features/projectGroups/state/projectGroupsSlice";
import { FolderIcon, DocumentIcon } from "@heroicons/react/outline";
import { PROJECT_GROUP_LIST_WITH_LIMIT_COMPONENT_ID } from "src/features/projectGroups/components/ProjectGroupList";
import { useTranslation } from "react-i18next";
import { createSafeWorker } from "src/common/components/Navigation/safeWorker";
import Fuse from "fuse.js";

const workerURL = new URL("./projectGroupSearch.worker.ts", import.meta.url);

export type SearchItem = {
  id: number | string;
  name: string;
  type: "projectGroup" | "project";
  // For groups, set projectGroupId === id in your data layer.
  projectGroupId: number | string;
};

function useDebounced<T>(value: T, ms: number) {
  const [v, setV] = useState(value);
  useEffect(() => {
    const id = setTimeout(() => setV(value), ms);
    return () => clearTimeout(id);
  }, [value, ms]);
  return v;
}

const ProjectGroupSearchBar = ({
  className,
  isFilteredByPublishedProjects,
  disabled,
}: {
  className?: string;
  isFilteredByPublishedProjects: boolean;
  disabled?: boolean;
}) => {
  const dispatch = useAppDispatch();
  const { t } = useTranslation();

  const searchIndex = useSelector(getSearchIndex) as SearchItem[];
  const { query, limit } = useSelector(getSearchState);

  const [results, setResults] = useState<SearchItem[]>([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [highlightedIndex, setHighlightedIndex] = useState<number>(-1);

  const wrapperRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const listRef = useRef<HTMLDivElement>(null);

  // Worker / fallback plumbing
  const workerRef = useRef<Worker | null>(null);
  const workerReadyRef = useRef(false);
  const modeRef = useRef<"worker" | "fallback">("worker");
  const latestQueryRef = useRef<string>("");

  // Fallback Fuse (built on-demand)
  const fuseRef = useRef<Fuse<SearchItem> | null>(null);

  // Build / rebuild worker or fallback index when data changes
  useEffect(() => {
    let terminated = false;

    // If already in fallback, rebuild Fuse and bail
    if (modeRef.current === "fallback") {
      const idx = Fuse.createIndex(["name"] as (keyof SearchItem)[], searchIndex);
      fuseRef.current = new Fuse<SearchItem>(
        searchIndex,
        { keys: ["name"], threshold: 0.4, includeScore: true, ignoreLocation: true },
        idx,
      );
      return () => {
        // cleanup for fallback path
        fuseRef.current = null;
      };
    }

    // Try to create worker
    const w = createSafeWorker(workerURL);
    if (!w) {
      // No workers available: fallback
      modeRef.current = "fallback";
      workerRef.current = null;
      workerReadyRef.current = false;

      const idx = Fuse.createIndex(["name"] as (keyof SearchItem)[], searchIndex);
      fuseRef.current = new Fuse<SearchItem>(
        searchIndex,
        { keys: ["name"], threshold: 0.4, includeScore: true, ignoreLocation: true },
        idx,
      );
      return () => {
        fuseRef.current = null;
      };
    }

    workerRef.current = w;
    workerReadyRef.current = false;

    const onMsg = (e: MessageEvent) => {
      const data = e.data as
        | { type: "READY" }
        | { type: "RESULTS"; payload: { q: string; items: SearchItem[] } };

      if (data.type === "READY") {
        workerReadyRef.current = true;
        return;
      }

      if (data.type === "RESULTS") {
        const { q, items } = data.payload;
        if (q === latestQueryRef.current) {
          setResults(items);
          setShowSuggestions(items.length > 0);
        }
      }
    };

    const onError = () => {
      // Worker crashed; go fallback
      if (!terminated) {
        modeRef.current = "fallback";
        workerRef.current?.terminate();
        workerRef.current = null;
        workerReadyRef.current = false;

        const idx = Fuse.createIndex(["name"] as (keyof SearchItem)[], searchIndex);
        fuseRef.current = new Fuse<SearchItem>(
          searchIndex,
          { keys: ["name"], threshold: 0.4, includeScore: true, ignoreLocation: true },
          idx,
        );
      }
    };

    w.addEventListener("message", onMsg);
    w.addEventListener("error", onError);
    // INIT safely (no guard needed; we just created it)
    w.postMessage({ type: "INIT", payload: { data: searchIndex } });

    return () => {
      terminated = true;
      w.removeEventListener("message", onMsg);
      w.removeEventListener("error", onError);
      w.terminate();
      workerRef.current = null;
      workerReadyRef.current = false;
    };
  }, [searchIndex]);

  // Debounce + defer query for buttery typing
  const debounced = useDebounced(query, 150);
  const deferred = useDeferredValue(debounced);

  useEffect(() => {
    const q = deferred.trim();
    latestQueryRef.current = q;

    if (!q) {
      setResults([]);
      setHighlightedIndex(-1);
      setShowSuggestions(false);
      return () => {
        /* */
      };
    }

    // Fallback mode (main thread) OR worker not ready
    if (modeRef.current === "fallback" || !workerRef.current || !workerReadyRef.current) {
      // Yield to browser; then do cheap vs fuzzy
      const id = setTimeout(() => {
        if (q.length < 3) {
          const qt = q.toLowerCase();
          const items = searchIndex.filter((x) => x.name.toLowerCase().includes(qt)).slice(0, 10);
          setResults(items);
          setShowSuggestions(items.length > 0);
          return;
        }
        if (!fuseRef.current) {
          const idx = Fuse.createIndex(["name"] as (keyof SearchItem)[], searchIndex);
          fuseRef.current = new Fuse<SearchItem>(
            searchIndex,
            { keys: ["name"], threshold: 0.4, includeScore: true, ignoreLocation: true },
            idx,
          );
        }
        const hits = fuseRef.current.search(q, { limit: 20 });
        hits.sort((a, b) => {
          const sA = a.score ?? 0,
            sB = b.score ?? 0;
          if (sA !== sB) return sA - sB;
          if (a.item.type !== b.item.type) return a.item.type === "projectGroup" ? -1 : 1;
          return a.item.name.localeCompare(b.item.name, undefined, { sensitivity: "base" });
        });
        const items = hits.slice(0, 10).map((h) => h.item);
        setResults(items);
        setShowSuggestions(items.length > 0);
      }, 0);

      return () => clearTimeout(id);
    }

    // Worker mode (READY + present)
    const w = workerRef.current;
    // Guard again for TypeScript sanity; if missing, just no-op cleanup
    if (!w)
      return () => {
        /* */
      };

    // Watchdog (optional but keeps UI responsive)
    const watchdog = setTimeout(() => {
      // If worker stalls after READY, switch to fallback next tick
      modeRef.current = "fallback";
      workerRef.current = null;
      workerReadyRef.current = false;

      const idx = Fuse.createIndex(["name"] as (keyof SearchItem)[], searchIndex);
      fuseRef.current = new Fuse<SearchItem>(
        searchIndex,
        { keys: ["name"], threshold: 0.4, includeScore: true, ignoreLocation: true },
        idx,
      );
    }, 750);

    w.postMessage({ type: "QUERY", payload: { q, limit: 10 } });

    return () => clearTimeout(watchdog);
  }, [deferred, searchIndex]);

  // Scroll selected item into view
  useEffect(() => {
    if (highlightedIndex >= 0 && listRef.current) {
      const el = listRef.current.children[highlightedIndex] as HTMLElement | undefined;
      el?.scrollIntoView({ block: "nearest" });
    }
    return () => {
      /* */
    };
  }, [highlightedIndex]);

  // Close dropdown on outside click
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target as Node)) {
        setShowSuggestions(false);
        setHighlightedIndex(-1);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => {
      document.removeEventListener("mousedown", handler);
    };
  }, []);

  const handleSelect = useCallback(
    (item: SearchItem) => {
      dispatch(setSearchQuery(item.name));
      dispatch(clearSort());
      // 👇 your requested cast
      dispatch(setSearchSelectedId(Number(item.projectGroupId)));
      dispatch(setSearchPage(0));
      setHighlightedIndex(-1);
      setShowSuggestions(false);

      dispatch(
        fetchProjectGroupsWithProjects({
          start: 0,
          limit,
          componentId: PROJECT_GROUP_LIST_WITH_LIMIT_COMPONENT_ID,
          // 👇 your requested cast
          projectGroupId: Number(item.projectGroupId),
          filterByPublishedProject: isFilteredByPublishedProjects,
        }),
      );
    },
    [dispatch, limit, isFilteredByPublishedProjects],
  );

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLInputElement>) => {
      if (e.key === "Enter" && results.length > 0) {
        e.preventDefault();
        const idx = highlightedIndex >= 0 ? highlightedIndex : 0;
        handleSelect(results[idx]);
        return;
      }
      if (!showSuggestions || results.length === 0) return;

      if (e.key === "ArrowDown") {
        e.preventDefault();
        setHighlightedIndex((prev) => Math.min(prev + 1, results.length - 1));
      } else if (e.key === "ArrowUp") {
        e.preventDefault();
        setHighlightedIndex((prev) => Math.max(prev - 1, 0));
      } else if (e.key === "Escape") {
        setShowSuggestions(false);
        setHighlightedIndex(-1);
      }
    },
    [showSuggestions, results, highlightedIndex, handleSelect],
  );

  const clearSearch = () => {
    dispatch(setSearchQuery(""));
    setResults([]);
    setShowSuggestions(false);
    setHighlightedIndex(-1);
    dispatch(setSearchSelectedId(0));

    dispatch(
      fetchProjectGroupsWithProjects({
        start: 0,
        limit,
        componentId: PROJECT_GROUP_LIST_WITH_LIMIT_COMPONENT_ID,
        filterByPublishedProject: isFilteredByPublishedProjects,
      }),
    );
  };

  return (
    <div
      className={`search-filter-project-groups ${className ?? ""}`}
      style={{ width: 250, ...(disabled ? { opacity: 0.5 } : {}) }}
    >
      <div
        ref={wrapperRef}
        className="relative w-full"
        role="combobox"
        aria-haspopup="listbox"
        aria-expanded={showSuggestions}
        aria-owns="pg-search-listbox"
        aria-controls="pg-search-listbox"
        aria-activedescendant={highlightedIndex >= 0 ? `pg-opt-${highlightedIndex}` : undefined}
      >
        <input
          ref={inputRef}
          type="text"
          className="w-full border border-gray-300 rounded-[10px] px-3 py-2 text-sm h-[46px] pr-8 focus:outline-none focus:ring-1 focus:ring-primary"
          placeholder={`${t("general.search")}...`}
          value={query}
          onChange={(e) => {
            const val = e.target.value;
            dispatch(setSearchQuery(val));
            if (val) setShowSuggestions(true);
          }}
          onFocus={() => {
            console.log("focus", document.activeElement);
            if (results.length > 0) setShowSuggestions(true);
          }}
          onKeyDown={handleKeyDown}
          aria-autocomplete="list"
          disabled={disabled}
          onBlur={() => console.log("blur")}
          onInput={(e) => console.log("onInput fired;", e)}
        />

        {query && (
          <button
            onClick={clearSearch}
            className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 text-lg"
            aria-label="Clear"
            type="button"
            disabled={disabled}
          >
            ×
          </button>
        )}

        {showSuggestions && (
          <div
            ref={listRef}
            id="pg-search-listbox"
            role="listbox"
            className="absolute top-full left-0 w-full bg-white border border-gray-300 rounded-[10px] mt-[1px] z-20 shadow-md max-h-64 overflow-y-auto overflow-x-hidden"
          >
            {results.length > 0 ? (
              results.map((item, idx) => {
                const isActive = idx === highlightedIndex;
                const parentGroupName =
                  item.type === "project" ? String(item.projectGroupId) : null;

                return (
                  <button
                    id={`pg-opt-${idx}`}
                    role="option"
                    aria-selected={isActive}
                    key={`${item.type}-${item.id}`}
                    onClick={() => handleSelect(item)}
                    className={`flex items-start w-full text-left px-3 py-2 text-sm ${
                      isActive ? "bg-gray-100" : "hover:bg-gray-50"
                    }`}
                    type="button"
                    disabled={disabled}
                  >
                    <span className="mt-[2px] shrink-0">
                      {item.type === "projectGroup" ? (
                        <FolderIcon className="h-5 w-5 text-gray-500" />
                      ) : (
                        <DocumentIcon className="h-5 w-5 text-gray-400" />
                      )}
                    </span>
                    <div className="ml-2 truncate">
                      <div className="font-medium text-sm text-gray-800 truncate">{item.name}</div>
                      {parentGroupName && (
                        <div className="text-xs text-gray-500 truncate">
                          {`${t("general.in")} '${parentGroupName}'`}
                        </div>
                      )}
                    </div>
                  </button>
                );
              })
            ) : (
              <div className="text-sm text-gray-500 px-4 py-3 italic">{t("general.noResults")}</div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default ProjectGroupSearchBar;
